/*
 * Inbox v2's state and data, apart from what draws them (`page.tsx`) and the writes
 * (`inbox-actions.ts`). Each list is its own query keyed by what it shows; a write reads again what
 * it made stale (`effects.ts`), so the rail's counts, the rows and the open thread agree.
 */
import { type UseQueryResult, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { AgentStatus } from "../../src/inbox-v2/agent-contract.ts";
import type {
  InboxConversation,
  InboxCounts,
  InboxListQuery,
  InboxPage,
  InboxViewQuery,
} from "../../src/inbox-v2/contract.ts";
import { agentListKey } from "../agent/agent-reads.ts";
import { agentDetailQuery } from "../agent/use-agent.ts";
import { ApiError } from "../http.ts";
import {
  accessQuery,
  countsQuery,
  listQuery as listOptions,
  needsReplyQuery,
  searchStatusQuery,
  threadQuery,
  viewsQuery,
} from "../queries.ts";
import type { MailBox, MailChoice } from "../shell-nav.ts";
import { type AgentListAnswer, failureReference, inboxApi } from "./api.ts";
import { assignmentOn } from "./assignment.tsx";
import type { ListFilters, ListSort } from "./list-prefs.ts";

export const NO_FILTERS: ListFilters = {
  status: null,
  agent: null,
  unread: false,
  days: null,
  check: false,
  label: null,
};

export type InboxView = {
  choice: MailChoice;
  /** Whose threads (the rail's scope), beside the place; null is everyone. */
  owner: string | null;
  q: string;
  filters: ListFilters;
  offset: number;
  sort: ListSort;
};

/** How many replies needing an answer the Highlights mode pins above the list. */
const HIGHLIGHTS_SHOWN = 3;

/**
 * The page's one report of what just happened: its sentence, Undo where the change can be put back
 * (resolving whether it was), and another way on where one applies, such as Try again (W5).
 */
export type Toast = {
  text: string;
  undo: (() => Promise<unknown>) | null;
  action?: { label: string; run: () => void };
};

export function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() =>
    typeof matchMedia === "undefined" ? true : matchMedia(query).matches,
  );
  useEffect(() => {
    const list = matchMedia(query);
    const change = () => setMatch(list.matches);
    list.addEventListener("change", change);
    return () => list.removeEventListener("change", change);
  }, [query]);
  return match;
}

export function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value.trim()), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export type SavedView = { id: string; name: string; query: Partial<InboxViewQuery> };

/*
 * What a choice asks the API for, before the search and the filters on top of it. A saved view is
 * its box and label here; the rest of its query (search, filters, order) is loaded into the page's
 * own search field, chips and order when it is opened, so the rep sees and can change it.
 */
export function choiceQuery(
  choice: MailChoice,
  views: readonly SavedView[],
): Partial<InboxListQuery> {
  switch (choice.kind) {
    case "box":
      return { box: choice.box };
    case "label":
      // A rail label row is every open thread with that label, whatever its status (LEA-485).
      return { box: "inbox", label: choice.label };
    case "meeting":
      return { box: "inbox", status: "meeting" };
    case "needs_reply":
      return { box: "inbox", status: "needs_reply" };
    case "follow_up":
      return { box: "inbox", status: "follow_up" };
    case "view": {
      // A view this person deleted elsewhere falls back to the inbox rather than to nothing. Its
      // label opens as the Label filter (`filtersOf`), so the chip says what narrows it.
      const query = views.find((view) => view.id === choice.id)?.query ?? {};
      return { box: query.box ?? "inbox" };
    }
  }
}

/** The box a choice lists from, which decides its empty sentence and whether label tabs apply. */
export function boxOf(choice: MailChoice, views: readonly SavedView[]): MailBox {
  return choiceQuery(choice, views).box ?? "inbox";
}

/** The saved order of a view the person saved, which is its order until they choose another. */
export function savedSort(choice: MailChoice, views: readonly SavedView[]): ListSort | null {
  if (choice.kind !== "view") return null;
  return views.find((view) => view.id === choice.id)?.query.sort ?? null;
}

/*
 * What the list asks the API for, without paging: the master inbox's flat list (no `section`), in
 * the view's order, with the expanded fields. The filters bar narrows it; nothing reorders it.
 */
export function viewListQuery(
  view: InboxView,
  views: readonly SavedView[],
): Partial<InboxListQuery> {
  return {
    ...narrowing(view, views),
    sort: view.sort,
    ...(view.filters.status ? { status: view.filters.status } : {}),
  };
}

/** The list's box or label, search and filters, apart from its order and Status. */
function narrowing(view: InboxView, views: readonly SavedView[]): Partial<InboxListQuery> {
  const { choice, filters, q } = view;
  return {
    ...choiceQuery(choice, views),
    ...(view.owner ? { assigned: view.owner } : {}),
    expand: "list",
    ...(q ? { q } : {}),
    ...(filters.unread ? { unread: "1" as const } : {}),
    ...(filters.days ? { days: filters.days } : {}),
    ...(filters.check ? { check: "1" as const } : {}),
    ...(filters.label ? { label: filters.label } : {}),
  };
}

/**
 * A reply agent state asks its own route under its own key, `agent`, with the same narrowing and
 * master status beside it, always newest first.
 */
function agentQuery(view: InboxView, views: readonly SavedView[]) {
  const agent = view.filters.agent;
  if (agent === null) return null;
  const status = view.filters.status;
  return { ...narrowing(view, views), ...(status ? { status } : {}), agent };
}

/** The list on screen, from the inbox's route or from the reply agent status filter's. */
export type ListAnswer = {
  page: InboxPage | undefined;
  failed: boolean;
  /** The server's reference for a failure it named, for the notice to quote; null otherwise. */
  reference: string | null;
  fetching: boolean;
  /**
   * Rows kept from the list shown before while the next one loads (a search typed, a view switched,
   * a page turned): they answer an older question, so the page dims them and settles nothing on them.
   */
  placeholder: boolean;
  refetch: () => void;
  /** The agent route's refusal to count past its limit, said where the rows would be. */
  refused: { limit: number; message: string } | null;
  /** Each reply agent status's total under the same narrowing; null off the agent route. */
  agentCounts: Readonly<Record<AgentStatus, number>> | null;
  /** The read was refused (401, 403): rows kept from before are this person's no longer (W3). */
  revoked: boolean;
};

const revokedBy = (error: unknown): boolean =>
  error instanceof ApiError && (error.signedOut || error.refused);

function inboxAnswer(query: UseQueryResult<InboxPage>): ListAnswer {
  return {
    page: query.data,
    failed: query.isError,
    reference: failureReference(query.error),
    fetching: query.isFetching,
    placeholder: query.isPlaceholderData,
    refetch: () => void query.refetch(),
    refused: null,
    agentCounts: null,
    revoked: revokedBy(query.error),
  };
}

function agentAnswer(query: UseQueryResult<AgentListAnswer>): ListAnswer {
  const answer = query.data;
  return {
    page: answer?.kind === "listed" ? answer.page : undefined,
    failed: query.isError,
    reference: failureReference(query.error),
    fetching: query.isFetching,
    placeholder: query.isPlaceholderData,
    refetch: () => void query.refetch(),
    refused: answer?.kind === "refused" ? { limit: answer.limit, message: answer.message } : null,
    agentCounts: answer?.kind === "listed" ? answer.counts : null,
    revoked: revokedBy(query.error),
  };
}

/** The work row "who answers next" reads from: the rail's first work row that holds any. */
export type NextPlace = "meeting" | "needs_reply";

/**
 * Who answers next follows the rail's work rows (LEA-494): Meeting requests while any wait, else Needs
 * reply, as before. Null while the counts are on their way, so the Highlights strip and the empty
 * pane's next reply wait for them rather than show Needs reply and then change their mind. Counts
 * that failed, or a server that never counts meeting requests, keep Needs reply.
 */
export function nextPlace(counts: InboxCounts | undefined, failed: boolean): NextPlace | null {
  if (counts === undefined) return failed ? "needs_reply" : null;
  return (counts.meeting ?? 0) > 0 ? "meeting" : "needs_reply";
}

/*
 * The Highlights strip's question: the same list, only the replies in the place who answers next
 * reads from (`nextPlace`), in the `next` order the reading pane's next reply uses (Interested and
 * Question first, a "Not a sales reply" nobody doubts last; `read-model.ts`).
 * It applies only to the inbox's first page, and not where the list already is about who answers
 * next (the Needs reply view, or a Status filter): there it would repeat or contradict the list.
 * It asks for the three it shows (`limit`), not a page of 50.
 */
export function highlightsQuery(
  query: Partial<InboxListQuery>,
  offset: number,
  place: NextPlace = "needs_reply",
) {
  if ((query.box ?? "inbox") !== "inbox" || query.status !== undefined || offset !== 0) return null;
  return {
    ...query,
    status: place,
    sort: "next" as const,
    limit: HIGHLIGHTS_SHOWN,
  };
}

/** What this person may reach in the workspace; one cache entry shared by the routes and the nav. */
export function useInboxAccess(workspaceId: string | undefined) {
  return useQuery({ ...accessQuery(workspaceId ?? ""), enabled: !!workspaceId });
}

/*
 * The workspace's inbox totals and this person's saved views. The rail reads them on every page of
 * an inbox workspace and the inbox page reads the same entries, so the two never disagree; every
 * write refreshes everything under `["inbox-v2", workspace]`. `enabled` is false where the workspace
 * has no inbox, so nothing is asked that the API would refuse.
 */
/** The inbox's totals, narrowed to one owner's threads where the rail's scope says so. */
export function useInboxCounts(workspaceId: string, enabled = true, owner: string | null = null) {
  return useQuery({ ...countsQuery(workspaceId, owner), enabled });
}

/**
 * The workspace switcher's badges: each inbox workspace's needs-reply replies, read one workspace at a
 * time, so a slow or failing workspace leaves only its own badge out (mitigation plan, F03). Each is
 * under its workspace's inbox key, so that inbox's own writes read it again.
 */
export function useNeedsReplyBadges(
  workspaceIds: readonly string[],
  enabled: boolean,
): ReadonlyMap<string, number> {
  const badges = useQueries({
    queries: workspaceIds.map((workspaceId) => ({ ...needsReplyQuery(workspaceId), enabled })),
  });
  return new Map(
    workspaceIds.flatMap((id, index) => {
      const unseen = badges[index]?.data;
      return unseen === undefined ? [] : [[id, unseen] as const];
    }),
  );
}

/** Whether search covers the whole list, read only while a search is typed (W1). */
export function useSearchStatus(workspaceId: string, searched: boolean) {
  return useQuery({ ...searchStatusQuery(workspaceId), enabled: searched });
}

/** How often an import still running is read again: a minute, as the note moves slowly. */
const IMPORT_REFRESH_MS = 60_000;

/**
 * The campaigns whose history is importing (step 10, Q3), for a person with the reply agent: read
 * once, then each minute while any is, and not again once none is.
 */
export function useImportProgress(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["inbox-v2", workspaceId, "backfill"],
    enabled,
    queryFn: ({ signal }) => inboxApi.backfill(workspaceId, signal),
    refetchInterval: (query) => (query.state.data?.length ? IMPORT_REFRESH_MS : false),
  });
}

/** How many replies needing an answer the empty reading pane offers to start with. */
const SHORTLIST_SHOWN = 3;

/**
 * The replies to answer first, for the reading pane while nothing is open (design review "Loading
 * and empty", P1), in the `next` order (`read-model.ts`): asked only while it is drawn, for the
 * expanded list (the only one whose `limit` the route honours) and only the three it shows, from the
 * place who answers next reads from, once the counts have said which (`nextPlace`).
 */
export function useNeedsReplyShortlist(
  workspaceId: string,
  enabled: boolean,
  owner: string | null,
  place: NextPlace | null,
) {
  return useQuery({
    ...shortlistQuery(workspaceId, owner, place ?? "needs_reply"),
    enabled: enabled && place !== null,
    select: (page) => page.conversations.slice(0, SHORTLIST_SHOWN),
  });
}

// The same question as the Highlights strip over the plain Inbox, so the two share one read (F14).
// Within the rail's scope: Mine's next reply is the viewer's own.
export const shortlistQuery = (
  workspaceId: string,
  owner: string | null = null,
  place: NextPlace = "needs_reply",
) =>
  listOptions(workspaceId, {
    box: "inbox",
    ...(owner ? { assigned: owner } : {}),
    expand: "list",
    status: place,
    sort: "next",
    limit: SHORTLIST_SHOWN,
  });

export function useInboxViews(workspaceId: string, enabled = true) {
  return useQuery({ ...viewsQuery(workspaceId), enabled });
}

/*
 * The rows on screen stay until the next list arrives (step 10, design review "Loading", P1): a
 * search typed, a view switched or a page turned keeps the last list shown, dimmed, instead of
 * blanking it to a loading line, across the inbox's route and the reply agent's alike. Never another
 * workspace's: a list kept across a switch of workspace would show one client's leads under another's
 * name for as long as the read takes. Only a first load, with nothing shown yet, has no rows. A
 * failure keeps them too, dimmed under its notice, so the rep keeps their place (P2); a refusal says
 * why in their place instead.
 */
function useKeptList(workspaceId: string, answer: ListAnswer): ListAnswer {
  const [shown, setShown] = useState<{ workspaceId: string; page: InboxPage } | null>(null);
  const settled = answer.page !== undefined && !answer.placeholder ? answer.page : undefined;
  useEffect(() => {
    if (settled) setShown({ workspaceId, page: settled });
  }, [workspaceId, settled]);
  const kept = shown?.workspaceId === workspaceId && !answer.revoked ? shown.page : undefined;
  // A refused read keeps nothing: not the rows from before, nor the rows its own cache still holds.
  if (answer.revoked) return { ...answer, page: undefined, placeholder: false };
  if (answer.page !== undefined || answer.refused || kept === undefined) return answer;
  return { ...answer, page: kept, placeholder: true };
}

export function useInboxData(
  workspaceId: string,
  view: InboxView,
  openId: string | null,
  highlights: boolean,
  /** False while a saved view's query is still being loaded into the page, which it waits for. */
  ready = true,
) {
  const counts = useInboxCounts(workspaceId, true, view.owner);
  const views = useInboxViews(workspaceId);
  const saved = views.data ?? [];
  const query = viewListQuery(view, saved);
  const byAgent = agentQuery(view, saved);
  // A saved view's list waits for its query, or it would flash the inbox first.
  const waiting = (view.choice.kind === "view" && views.isPending) || !ready;
  const main = useQuery({
    ...listOptions(workspaceId, query, view.offset),
    enabled: !waiting && byAgent === null,
  });
  const agentList = useQuery({
    queryKey: [...agentListKey(workspaceId), byAgent, view.offset],
    enabled: !waiting && byAgent !== null,
    queryFn: async ({ signal }) => {
      if (byAgent === null) throw new Error("agent_list_without_status");
      return inboxApi.agentList(workspaceId, { ...byAgent, offset: view.offset }, signal);
    },
    // A repeatable read over up to 1,500 candidates: read again on a rep's act, not on every focus.
    refetchOnWindowFocus: false,
  });
  const list = useKeptList(
    workspaceId,
    byAgent === null ? inboxAnswer(main) : agentAnswer(agentList),
  );
  // The strip answers who replies next; beside a reply agent status it would answer another question.
  // It waits for the counts, which say whether that is Meeting requests or Needs reply (LEA-494).
  const place = nextPlace(counts.data, counts.isError);
  const pinned =
    highlights && byAgent === null && place !== null
      ? highlightsQuery(query, view.offset, place)
      : null;
  const strip = useQuery({
    ...listOptions(workspaceId, pinned ?? {}),
    enabled: !waiting && pinned !== null,
  });
  const thread = useQuery({
    ...threadQuery(workspaceId, openId ?? "", assignmentOn(workspaceId, counts.data)),
    enabled: openId !== null,
  });
  // The pinned replies stay in the list below them, so J and K and "select all" walk the list.
  const visible = list.page?.conversations ?? [];
  return {
    counts,
    views: saved,
    list,
    strip:
      pinned && strip.data && strip.data.total > 0
        ? {
            place: pinned.status,
            rows: strip.data.conversations.slice(0, HIGHLIGHTS_SHOWN),
            total: strip.data.total,
          }
        : null,
    thread,
    visible,
  };
}

/**
 * The conversations either side of the open one, read ahead once it has loaded, so J and K land on a
 * thread already in hand, as the reply agent's inbox did (step 10). With the agent, its plan too,
 * under the key its timeline item reads. The same reads the page makes; they change nothing.
 */
export function usePrefetchNeighbours(
  workspaceId: string,
  visible: readonly InboxConversation[],
  open: { id: string | null; loaded: boolean },
  agent: boolean,
  withAssignee: boolean,
) {
  const client = useQueryClient();
  const index = visible.findIndex((row) => row.id === open.id);
  const around = [visible[index + 1], visible[index - 1]]
    .flatMap((row) => (index !== -1 && row ? [row.id] : []))
    .join(" ");
  useEffect(() => {
    if (!open.loaded) return;
    for (const id of around.split(" ").filter(Boolean)) {
      // The reader's own options, freshness included: within it, opening the neighbour asks nothing.
      void client.prefetchQuery(threadQuery(workspaceId, id, withAssignee));
      if (agent) void client.prefetchQuery(agentDetailQuery(workspaceId, id));
    }
  }, [open.loaded, around, workspaceId, agent, withAssignee, client]);
}

function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
    target.closest("[role=menu], [role=dialog]") !== null
  );
}

/**
 * Enter on a focused control is that control's own: a button presses, a link follows. A list row is
 * the exception: Enter opens the row J and K moved to, which focus may have stayed behind.
 */
const pressing = (event: KeyboardEvent): boolean =>
  event.key === "Enter" &&
  event.target instanceof Element &&
  event.target.closest("button:not(.lcr-row), a[href], [role=button]") !== null;

/**
 * A key the page leaves alone. One held with a modifier is the browser's or the system's. One that
 * something nearer the focus already acted on is spent: the Escape that closed the shortcuts panel
 * (the shell's) must not also clear the selection under it.
 */
const notOurs = (event: KeyboardEvent): boolean =>
  event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey;

/** The page's keys: Esc always, the rest only when the rep is not typing. */
export function useKeys(keys: Record<string, () => void>, onEscape: () => void): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (notOurs(event)) return;
      if (event.key === "Escape") return onEscape();
      if (typing(event.target) || pressing(event)) return;
      const run = keys[event.key];
      if (!run) return;
      event.preventDefault();
      run();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
}

export function useToast() {
  const [toast, setToast] = useState<Toast | null>(null);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 10_000);
    return () => clearTimeout(timer);
  }, [toast]);
  return [toast, setToast] as const;
}
